import React, { useEffect, useLayoutEffect, useState } from 'react';
import LandingPage from './components/LandingPage';
import MainContainer from './components/MainContainer';
import './App.css';

const THEME_STORAGE_KEY = 'value-investment-theme';
const SESSION_STORAGE_KEY = 'value-investment-session-token';
const GUEST_STORAGE_KEY = 'value-investment-guest';

const getInitialTheme = () => {
  try {
    const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (savedTheme === 'light' || savedTheme === 'dark') return savedTheme;
  } catch (storageError) {
    // The UI can still follow the system preference when storage is unavailable.
  }

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
};

const getInitialSessionToken = () => {
  try {
    return window.localStorage.getItem(SESSION_STORAGE_KEY) || null;
  } catch (storageError) {
    return null;
  }
};

const getInitialGuestFlag = () => {
  try {
    return window.localStorage.getItem(GUEST_STORAGE_KEY) === 'true';
  } catch (storageError) {
    return false;
  }
};

const persistGuestFlag = (isGuest) => {
  try {
    if (isGuest) {
      window.localStorage.setItem(GUEST_STORAGE_KEY, 'true');
    } else {
      window.localStorage.removeItem(GUEST_STORAGE_KEY);
    }
  } catch (storageError) {
    // Best effort; in-memory state still reflects the choice.
  }
};

const App = () => {
  const [theme, setTheme] = useState(getInitialTheme);
  const [view, setView] = useState('valuation');
  const [ncavData, setNcavData] = useState([]);
  const [epsxData, setEpsxData] = useState([]);
  const [cspData, setCspData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionToken, setSessionToken] = useState(getInitialSessionToken);
  const [isGuest, setIsGuest] = useState(getInitialGuestFlag);
  const [showLoginForm, setShowLoginForm] = useState(false);

  useLayoutEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch (storageError) {
      // A private browsing policy may prevent persistence; the toggle still works.
    }
  }, [theme]);

  // The screener data itself is public and loads regardless of login state.
  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const [ncavResponse, epsxResponse, cspResponse] = await Promise.all([
          fetch(`${apiUrl}/api/ncav`, { signal: controller.signal }),
          fetch(`${apiUrl}/api/epsx`, { signal: controller.signal }),
          fetch(`${apiUrl}/api/csp`, { signal: controller.signal }),
        ]);

        if (!ncavResponse.ok || !epsxResponse.ok || !cspResponse.ok) {
          throw new Error('The valuation data could not be loaded.');
        }

        const [ncav, epsx, csp] = await Promise.all([
          ncavResponse.json(),
          epsxResponse.json(),
          cspResponse.json(),
        ]);

        setNcavData(Array.isArray(ncav) ? ncav : []);
        setEpsxData(Array.isArray(epsx) ? epsx : []);
        setCspData(Array.isArray(csp) ? csp : []);
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setError(requestError);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => controller.abort();
  }, []);

  // A stored session token is validated once; a rejected one (expired,
  // revoked, or superseded by a login elsewhere) is dropped silently.
  useEffect(() => {
    if (!sessionToken) return undefined;

    const controller = new AbortController();

    const verifySession = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${apiUrl}/api/auth/me`, {
          headers: { Authorization: `Bearer ${sessionToken}` },
          signal: controller.signal,
        });

        if (response.status === 401) {
          setSessionToken(null);
          try {
            window.localStorage.removeItem(SESSION_STORAGE_KEY);
          } catch (storageError) {
            // Best effort; the in-memory state is already cleared.
          }
        }
      } catch (requestError) {
        // A transient network error shouldn't log the user out.
      }
    };

    verifySession();
    return () => controller.abort();
  }, [sessionToken]);

  const handleLoginSuccess = (newSessionToken) => {
    setSessionToken(newSessionToken);
    try {
      window.localStorage.setItem(SESSION_STORAGE_KEY, newSessionToken);
    } catch (storageError) {
      // Best effort; the session still works for this tab.
    }
    setIsGuest(false);
    persistGuestFlag(false);
    setShowLoginForm(false);
  };

  const handleContinueAsGuest = () => {
    setIsGuest(true);
    persistGuestFlag(true);
    setShowLoginForm(false);
  };

  const handleShowLogin = () => {
    setShowLoginForm(true);
  };

  const handleLogout = () => {
    const currentToken = sessionToken;
    setSessionToken(null);
    try {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (storageError) {
      // Best effort; the in-memory state is already cleared.
    }
    setIsGuest(true);
    persistGuestFlag(true);
    setShowLoginForm(false);

    if (currentToken) {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      fetch(`${apiUrl}/api/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${currentToken}` },
      }).catch(() => {
        // Best effort; the local session is already cleared either way.
      });
    }
  };

  const authenticated = Boolean(sessionToken);
  const showLanding = !authenticated && (!isGuest || showLoginForm);

  return (
    <div className="App">
      {showLanding ? (
        <LandingPage
          onLoginSuccess={handleLoginSuccess}
          onContinueAsGuest={handleContinueAsGuest}
        />
      ) : (
        <MainContainer
          theme={theme}
          onToggleTheme={() => setTheme((currentTheme) => (
            currentTheme === 'light' ? 'dark' : 'light'
          ))}
          view={view}
          onChangeView={setView}
          ncavData={ncavData}
          epsxData={epsxData}
          cspData={cspData}
          loading={loading}
          error={error}
          isAuthenticated={authenticated}
          onShowLogin={handleShowLogin}
          onLogout={handleLogout}
        />
      )}
    </div>
  );
};

export { getInitialTheme, THEME_STORAGE_KEY, SESSION_STORAGE_KEY, GUEST_STORAGE_KEY };
export default App;
